/**
 * Handles table of contents
 *
 * @package AGDS
 * @category Modules
 * @module TableOfContents
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class TableOfContents {
    constructor({ contentSelector, tocSelector, headingSelector = "h2", headerHeight = 0 }) {
        this.contentEl = document.querySelector(contentSelector);
        this.tocEl = document.querySelector(tocSelector);
        this.headingSelector = headingSelector;
        this.headings = [];
        this.tocLinks = [];
        this.observer = null;
        this.headerHeight = headerHeight;
    }

    init() {
        if (!this.contentEl || !this.tocEl) return;

        this.collectHeadings();
        this.injectIds();
        this.renderToc();
        this.updateChapterCount();
        this.bindClickScroll();
        this.initScrollSpy();
    }

    collectHeadings() {
        this.headings = Array.from(this.contentEl.querySelectorAll(this.headingSelector));
    }

    slugify(text, index) {
        const charMap = {
            а: "a",
            б: "b",
            в: "v",
            г: "h",
            ґ: "g",
            д: "d",
            е: "e",
            є: "ye",
            ж: "zh",
            з: "z",
            и: "y",
            і: "i",
            ї: "yi",
            й: "j",
            к: "k",
            л: "l",
            м: "m",
            н: "n",
            о: "o",
            п: "p",
            р: "r",
            с: "s",
            т: "t",
            у: "u",
            ф: "f",
            х: "kh",
            ц: "ts",
            ч: "ch",
            ш: "sh",
            щ: "shch",
            ь: "",
            ю: "yu",
            я: "ya",
        };

        return (
            text
                .toLowerCase()
                .trim()
                .split("")
                .map((char) => charMap[char] || (/[a-z0-9]/.test(char) ? char : "-"))
                .join("")
                .replace(/-+/g, "-")
                .replace(/^-|-$/g, "") || `heading-${index + 1}`
        );
    }

    injectIds() {
        this.headings.forEach((heading, index) => {
            const text = heading.textContent.trim();
            const slug = this.slugify(text, index);
            const span = document.createElement("span");

            span.id = slug;
            span.textContent = text;
            heading.textContent = "";
            heading.appendChild(span);
            this.headings[index] = span;
        });
    }

    renderToc() {
        const fragment = document.createDocumentFragment();

        this.headings.forEach((span) => {
            const item = document.createElement("li");
            const link = document.createElement("a");

            link.href = `#${span.id}`;
            link.innerHTML = `<span>${span.textContent}</span>`;
            item.appendChild(link);
            fragment.appendChild(item);
            this.tocLinks.push(link);
        });

        this.tocEl.innerHTML = "";
        this.tocEl.appendChild(fragment);
    }

    bindClickScroll() {
        this.tocLinks.forEach((link) => {
            link.addEventListener("click", (event) => {
                event.preventDefault();

                const targetId = link.getAttribute("href").slice(1);
                const targetEl = document.getElementById(targetId);
                if (!targetEl) return;

                const y = targetEl.getBoundingClientRect().top + window.pageYOffset - this.headerHeight - 20;
                window.scrollTo({ top: y, behavior: "smooth" });
            });
        });
    }

    initScrollSpy() {
        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const id = entry.target.id;
                    const linkParent = this.tocEl.querySelector(`a[href="#${id}"]`)?.parentElement;

                    if (!entry.isIntersecting) return;

                    this.tocEl.querySelectorAll("._active").forEach((el) => el.classList.remove("_active"));
                    linkParent?.classList.add("_active");
                });
            },
            {
                root: null,
                rootMargin: `-${this.headerHeight + 5}px 0px -80% 0px`,
                threshold: 0,
            },
        );

        this.headings.forEach((span) => this.observer.observe(span));
    }

    /**
     * Updates the chapter count in the TOC header.
	 * 
     */
    updateChapterCount() {
        const countElement = document.querySelector(".table-of-contents-count");
        if (!countElement) return;

        const totalChapters = this.headings.length;
        const rawText = countElement.textContent.replace(/[·\d\s]/g, "");
        countElement.textContent = `· ${totalChapters} ${rawText}`;
    }
}
